import {
  User,
  getAuth,
  inMemoryPersistence,
  signInAnonymously,
  signOut,
} from 'firebase/auth';
import { makeAutoObservable, runInAction } from 'mobx';

import { GTMService } from '@services';

import { app } from '../firebase.config';
import { CloudFunctionsService } from '../services/CloudFunctions';
import { FirebaseService } from '../services/Firebase';
import { sendEventToHost } from '../utils';
import { rollbar } from '../utils/rollbar';

export default class UserStore {
  user?: User;
  userLoading = false;
  userHasError?: Error;
  isSignedIn = false;
  ip = '';
  firebaseService = new FirebaseService(new CloudFunctionsService());
  gtmService = GTMService.getInstance();
  gaClientId = '';

  constructor() {
    makeAutoObservable(this);

    this.login();
  }

  fetchIP = async () => {
    try {
      const res = await this.firebaseService.getUserIp();
      runInAction(() => {
        // getIp_v1 returns null when no IP is present (e.g. missing x-forwarded-for on a local run).
        this.ip = res.data != null ? String(res.data) : '';
      });
    } catch (error) {
      rollbar.warning('getUserIp failed', { error });
      runInAction(() => {
        this.ip = '';
      });
    }
  };

  login = () => {
    if (!this.userLoading) {
      if (this.user) {
        this.signOutAndSignInAnonymously();
      } else {
        this.signIn();
      }
    }
  };

  signOutAndSignInAnonymously = async () => {
    try {
      runInAction(() => {
        this.userLoading = true;
      });
      const auth = getAuth(app);
      auth.setPersistence(inMemoryPersistence);

      await signOut(auth).then(() => {
        auth.onAuthStateChanged(async (user) => {
          if (!user) {
            await signInAnonymously(auth).then((credentials) => {
              runInAction(() => {
                this.user = credentials.user;
              });
            });
            this.isSignedIn = true;
          }
        });
      });
    } catch (error) {
      rollbar.error(
        'User store: Error occurred while re-authenticating user',
        error as Error
      );
      sendEventToHost('error', { data: { error: error as Error } });
    } finally {
      runInAction(() => {
        this.userLoading = false;
      });
    }
  };

  signIn = async () => {
    try {
      runInAction(() => {
        this.userLoading = true;
      });
      const auth = getAuth(app);
      auth.setPersistence(inMemoryPersistence);

      await signInAnonymously(auth).then((credentials) => {
        runInAction(() => {
          this.user = credentials.user;
        });
      });
      this.isSignedIn = true;
    } catch (error) {
      rollbar.error(
        'User store: Error occurred while authenticating user',
        error as Error
      );
      sendEventToHost('error', { data: { error: error as Error } });
    } finally {
      runInAction(() => {
        this.userLoading = false;
      });
    }
  };
}
